// src/lib/shiftApi.js
// 班別管理系統前端 API client（獨立 axios 實例，token 存 localStorage）
import axios from 'axios';
import { io } from 'socket.io-client';

export const SHIFT_TOKEN_KEY = 'shift_token';

export function getShiftToken() {
  try { return localStorage.getItem(SHIFT_TOKEN_KEY) || ''; } catch { return ''; }
}
export function setShiftToken(t) {
  try { if (t) localStorage.setItem(SHIFT_TOKEN_KEY, t); else localStorage.removeItem(SHIFT_TOKEN_KEY); } catch { /* ignore */ }
}

const http = axios.create({ baseURL: '/api/shift', timeout: 15000, withCredentials: true });
http.interceptors.request.use((config) => {
  const t = getShiftToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const adminHttp = axios.create({ baseURL: '/api/admin/shift', timeout: 15000 });
adminHttp.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem('admin_token');
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } catch { /* ignore */ }
  return config;
});

// ── 認證 ──
// 僅用員工代號（4–6 碼數字）登入
export const shiftLogin = (code) => http.post('/auth/login', { code: String(code || '').trim() }).then(r => r.data);
export const shiftMe = () => http.get('/auth/me').then(r => r.data);
export const shiftLogout = () => http.post('/auth/logout').then(r => r.data);

// ── TaskNode 樹 ──
export const fetchTree = (params = {}) => http.get('/tree', { params }).then(r => r.data);
export const createExtraNode = (payload) => http.post('/tree', payload).then(r => r.data);
export const fetchExtras = () => http.get('/tree/extras').then(r => r.data);
export const patchExtraAssignments = (id, payload) => http.patch(`/tree/${id}/assignments`, payload).then(r => r.data);
export const fetchTreeProgress = (bizDate) => http.get('/tree/progress', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const patchTreeNode = (id, payload) => http.patch(`/tree/${id}`, payload).then(r => r.data);
export const patchMySchedule = (payload) => http.patch('/my-schedule', payload).then(r => r.data);
export const fetchDayNotes = (bizDate) => http.get('/day-notes', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const putDayNote = (payload) => http.put('/day-notes', payload).then(r => r.data);
export const postDayNote = (payload) => http.post('/day-notes', payload).then(r => r.data);
export const fetchAvailableShifts = (bizDate) =>
  http.get('/available-shifts', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);

// ── 表單（舊版相容）──
export const fetchActiveForms = () => http.get('/forms').then(r => r.data);
export const fetchAllForms = () => http.get('/forms/all').then(r => r.data);
export const fetchForm = (id, bizDate) => http.get(`/forms/${id}`, { params: bizDate ? { bizDate } : {} }).then(r => r.data);
export const patchItem = (formId, itemId, payload) => http.patch(`/forms/${formId}/items/${itemId}`, payload).then(r => r.data);
export const submitForm = (formId, payload) => http.post(`/forms/${formId}/submit`, payload).then(r => r.data);

// ── 確認 pill / 備註 ──
export const fetchConfirmation = (formId, bizDate) => http.get(`/forms/${formId}/confirmation`, { params: bizDate ? { bizDate } : {} }).then(r => r.data);
export const patchConfirmation = (formId, payload) => http.patch(`/forms/${formId}/confirmation`, payload).then(r => r.data);

// ── 歷史（老闆）──
export const fetchHistory = () => http.get('/history').then(r => r.data);
export const fetchHistoryDetail = (date) => http.get(`/history/${date}`).then(r => r.data);
export const archiveHistory = (date) => http.post(`/history/${date}/archive`).then(r => r.data);

// ── 閒置代辦 ──
export const fetchIdleTasks = () => http.get('/idle-tasks').then(r => r.data);
export const completeIdleTask = (id) => http.post(`/idle-tasks/${id}/complete`).then(r => r.data);

// ── 排班 / 指派任務 / 異常回報（v2）──
export const fetchMySchedule = (bizDate) => http.get('/my-schedule', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const fetchMyTasks = (bizDate) => http.get('/tasks', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const completeTask = (id, note) => http.post(`/tasks/${id}/complete`, { note }).then(r => r.data);
export const uncompleteTask = (id) => http.post(`/tasks/${id}/uncomplete`).then(r => r.data);
export const assignTask = (payload) => http.post('/tasks', payload).then(r => r.data);
export const fetchTaskOverview = (bizDate) => http.get('/tasks/overview', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const createAnomaly = (payload) => http.post('/anomalies', payload).then(r => r.data);
export const uploadAnomalyImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return http.post('/anomalies/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const fetchMyAnomalies = () => http.get('/anomalies/mine').then(r => r.data);
export const fetchTeamAnomalies = (bizDate) => http.get('/anomalies', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const patchAnomaly = (id, status, resolvedNote) =>
  http.patch(`/anomalies/${id}`, { status, resolvedNote }).then(r => r.data);

// ── 今日班表（主管／老闆）──
export const fetchRoster = (bizDate) => http.get('/roster', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const putRoster = (payload) => http.put('/roster', payload).then(r => r.data);

// ── 月排班／可上時段 ──
export const fetchMyAvailability = (month) =>
  http.get('/schedule-month/availability', { params: { month } }).then(r => r.data);
export const saveMyAvailability = (payload) =>
  http.put('/schedule-month/availability', payload).then(r => r.data);
export const fetchMyMonthAssignments = (month, view = 'mine') =>
  http.get('/schedule-month/my-assignments', { params: { month, view } }).then(r => r.data);
export const patchShiftScheduleCell = (payload) =>
  http.patch('/schedule-month/cell', payload).then(r => r.data);

// ── 表單編輯 ──
export const createForm = (payload) => http.post('/forms', payload).then(r => r.data);
export const patchFormMeta = (id, payload) => http.patch(`/forms/${id}/meta`, payload).then(r => r.data);
export const disableForm = (id) => http.delete(`/forms/${id}`).then(r => r.data);
export const addSection = (formId, payload) => http.post(`/forms/${formId}/sections`, payload).then(r => r.data);
export const patchSection = (formId, sid, payload) => http.patch(`/forms/${formId}/sections/${sid}`, payload).then(r => r.data);
export const deleteSection = (formId, sid) => http.delete(`/forms/${formId}/sections/${sid}`).then(r => r.data);
export const reorderSections = (formId, orders) => http.patch(`/forms/${formId}/sections/reorder`, { orders }).then(r => r.data);
export const addItem = (formId, sid, payload) => http.post(`/forms/${formId}/sections/${sid}/items`, payload).then(r => r.data);
export const patchFormItem = (formId, sid, iid, payload) => http.patch(`/forms/${formId}/sections/${sid}/items/${iid}`, payload).then(r => r.data);
export const deleteItem = (formId, sid, iid) => http.delete(`/forms/${formId}/sections/${sid}/items/${iid}`).then(r => r.data);
export const reorderItems = (formId, sid, orders) => http.patch(`/forms/${formId}/sections/${sid}/items/reorder`, { orders }).then(r => r.data);
export const uploadItemImage = (formId, iid, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return http.post(`/forms/${formId}/items/${iid}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deleteItemImage = (formId, iid, imgId) => http.delete(`/forms/${formId}/items/${iid}/images/${imgId}`).then(r => r.data);

// ── 後台管理 API ──
export const adminFetchStaff = () => adminHttp.get('/staff').then(r => r.data);
export const adminCreateStaff = (payload) => adminHttp.post('/staff', payload).then(r => r.data);
export const adminPatchStaff = (phone, payload) => adminHttp.patch(`/staff/${phone}`, payload).then(r => r.data);
export const adminResetPassword = (phone, password) => adminHttp.post(`/staff/${phone}/reset-password`, { password }).then(r => r.data);
export const adminDeleteStaff = (phone) => adminHttp.delete(`/staff/${phone}`).then(r => r.data);

export const adminFetchForms = () => adminHttp.get('/forms').then(r => r.data);
export const adminCreateForm = (payload) => adminHttp.post('/forms', payload).then(r => r.data);
export const adminPatchForm = (id, payload) => adminHttp.patch(`/forms/${id}`, payload).then(r => r.data);
export const adminRestoreForm = (id) => adminHttp.post(`/forms/${id}/restore`).then(r => r.data);
export const adminDeleteForm = (id) => adminHttp.delete(`/forms/${id}`).then(r => r.data);

export const adminFetchIdleTasks = () => adminHttp.get('/idle-tasks').then(r => r.data);
export const adminCreateIdleTask = (payload) => adminHttp.post('/idle-tasks', payload).then(r => r.data);
export const adminPatchIdleTask = (id, payload) => adminHttp.patch(`/idle-tasks/${id}`, payload).then(r => r.data);
export const adminResetCooldown = (id) => adminHttp.post(`/idle-tasks/${id}/reset-cooldown`).then(r => r.data);
export const adminDeleteIdleTask = (id) => adminHttp.delete(`/idle-tasks/${id}`).then(r => r.data);

export const adminFetchDashboard = () => adminHttp.get('/dashboard').then(r => r.data);
export const adminFetchHistory = () => adminHttp.get('/history').then(r => r.data);
export const adminFetchHistoryDetail = (date) => adminHttp.get(`/history/${date}`).then(r => r.data);

// ── 後台 TaskNode ──
export const adminFetchTree = () => adminHttp.get('/tree').then(r => r.data);
export const adminPreviewTree = (params) => adminHttp.get('/tree/preview', { params }).then(r => r.data);
export const adminCreateNode = (payload) => adminHttp.post('/tree', payload).then(r => r.data);
export const adminPatchNode = (id, payload) => adminHttp.patch(`/tree/${id}`, payload).then(r => r.data);
export const adminReorderNodes = (orders) => adminHttp.post('/tree/reorder', { orders }).then(r => r.data);
export const adminDeleteNode = (id) => adminHttp.delete(`/tree/${id}`).then(r => r.data);
export const adminUploadTreeImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return adminHttp.post('/tree/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// ── 近日公告（後台）──
export const adminFetchAnnouncements = () => adminHttp.get('/announcements').then(r => r.data);
export const adminCreateAnnouncement = (payload) => adminHttp.post('/announcements', payload).then(r => r.data);
export const adminPatchAnnouncement = (id, payload) => adminHttp.patch(`/announcements/${id}`, payload).then(r => r.data);
export const adminDeleteAnnouncement = (id) => adminHttp.delete(`/announcements/${id}`).then(r => r.data);
export const adminUploadAnnouncementImage = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return adminHttp.post('/announcements/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// ── 近日公告（員工端）──
export const fetchActiveAnnouncements = () => http.get('/announcements/active').then(r => r.data);
export const ackAnnouncement = (id) => http.post(`/announcements/${id}/ack`).then(r => r.data);
export const fetchAnnouncementAcks = (id) => http.get(`/announcements/${id}/acks`).then(r => r.data);

export const adminFetchTodayOverview = (bizDate) =>
  adminHttp.get('/today-overview', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const adminFetchOverview = (bizDate) =>
  adminHttp.get('/overview', { params: bizDate ? { date: bizDate } : {} }).then(r => r.data);
export const adminFetchTrackingSheet = (params = {}) =>
  adminHttp.get('/tracking/sheet', { params }).then(r => r.data);
export const adminFetchNodeHistory = (id, params = {}) =>
  adminHttp.get(`/tracking/node/${id}/history`, { params }).then(r => r.data);
export const adminClearNodeValidity = (id, sourceBizDate) =>
  adminHttp.post(`/tracking/node/${id}/clear-validity`, { sourceBizDate }).then(r => r.data);
export const adminFetchWeekShifts = () => adminHttp.get('/week-shifts').then(r => r.data);
export const adminPutWeekShifts = (rows) => adminHttp.put('/week-shifts', { rows }).then(r => r.data);

// ── WebSocket ──
export function connectShiftSocket() {
  return io('/shift-ws', {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
  });
}

// ── 前端顯示用時間工具（與後端 lib/shift-logic 同規則）──
const DAYS_LBL = ['日', '一', '二', '三', '四', '五', '六'];
export function weekdayLabel(wd) { return DAYS_LBL[wd] ?? ''; }

export default http;
